import React, { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import Button from './Button';
import Input from './Input';
import Modal from './Modal';
import { useSnackbar } from '../../contexts/SnackbarContext';

// Icons
const ChevronDownIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);

const CrossIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
);

interface Teacher {
    id: number;
    name: string;
    email?: string;
}

interface TeacherSelectorProps {
    teachers: Teacher[];
    selectedTeachers: Teacher[];
    onChange: (teachers: Teacher[]) => void;
    onAddTeacher?: (teacherData: { name: string; email: string; teacher_code: string }) => Promise<any>;
    placeholder?: string;
    disabled?: boolean;
}

const TeacherSelector: React.FC<TeacherSelectorProps> = ({
    teachers,
    selectedTeachers,
    onChange,
    onAddTeacher,
    placeholder = 'Select Teachers',
    disabled = false
}) => {
    const { showSuccess, showError } = useSnackbar();
    const [isOpen, setIsOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState({});
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newTeacherData, setNewTeacherData] = useState({ name: '', email: '' });
    const [isAddingTeacher, setIsAddingTeacher] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const selectedTeachersRef = useRef(selectedTeachers);
    const teachersRef = useRef(teachers);

    // Keep refs in sync with props
    useEffect(() => {
        selectedTeachersRef.current = selectedTeachers;
    }, [selectedTeachers]);

    useEffect(() => {
        teachersRef.current = teachers;
    }, [teachers]);

    // Helper function to generate teacher code from name
    const generateTeacherCode = useCallback((name: string): string => {
        if (!name) return `teacher-${uuidv4().substring(0, 4)}`;
        const baseCode = name.toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        const shortSuffix = uuidv4().substring(0, 4);
        return `${baseCode.substring(0, 15)}-${shortSuffix}`;
    }, []);

    const updatePanelPosition = useCallback(() => {
        if (!buttonRef.current) return;

        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const panelHeightEstimate = 240;
        const margin = 4;

        let top: number | 'auto' = 'auto';
        let bottom: number | 'auto' = 'auto';

        if (spaceBelow >= panelHeightEstimate || spaceBelow >= spaceAbove) {
            top = rect.bottom + window.scrollY + margin;
        } else {
            bottom = window.innerHeight - rect.top - window.scrollY + margin;
        }

        setPanelStyle({
            position: 'absolute',
            top: top === 'auto' ? 'auto' : `${top}px`,
            bottom: bottom === 'auto' ? 'auto' : `${bottom}px`,
            left: `${rect.left + window.scrollX}px`,
            width: `${rect.width}px`,
            zIndex: 1100,
        });
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) return;

        updatePanelPosition();

        const handleResize = () => updatePanelPosition();
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize, true);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize, true);
        };
    }, [isOpen, updatePanelPosition]);

    // Reset search term when dropdown closes
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const panelElement = document.getElementById('teacher-selector-panel');
            if (
                dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
                (!panelElement || !panelElement.contains(event.target as Node))
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleOptionToggle = useCallback((teacher: Teacher) => {
        const isSelected = selectedTeachers.some(t => t.id === teacher.id);
        const newSelectedTeachers = isSelected
            ? selectedTeachers.filter(t => t.id !== teacher.id)
            : [...selectedTeachers, teacher];
        onChange(newSelectedTeachers);
    }, [selectedTeachers, onChange]);

    const handleClearSelection = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange([]);
        setIsOpen(false);
    };

    const handleAddTeacherClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsAddModalOpen(true);
        setIsOpen(false);
    };

    const handleAddTeacherInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewTeacherData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveNewTeacher = async () => {
        if (!newTeacherData.name || !newTeacherData.email) {
            showError('Name and email are required.');
            return;
        }

        if (!/\S+@\S+\.\S+/.test(newTeacherData.email)) {
            showError('Please enter a valid email address.');
            return;
        }

        if (!onAddTeacher) {
            showError('Add teacher functionality is not available.');
            return;
        }

        setIsAddingTeacher(true);

        try {
            const generatedCode = generateTeacherCode(newTeacherData.name.trim());

            const dataToSend = {
                name: newTeacherData.name.trim(),
                email: newTeacherData.email.trim(),
                teacher_code: generatedCode
            };

            // Call onAddTeacher and get the newly created teacher
            const newTeacher = await onAddTeacher(dataToSend);

            console.log('[TeacherSelector] Teacher returned from onAddTeacher:', newTeacher);

            showSuccess('Teacher added successfully!');
            setIsAddModalOpen(false);

            const teacherName = newTeacherData.name.trim();
            const teacherEmail = newTeacherData.email.trim();
            setNewTeacherData({ name: '', email: '' });

            // Select the newly added teacher
            // Try using the returned teacher first, otherwise find it in the updated list
            // Use multiple attempts with increasing delays to ensure the teachers list is updated
            const attemptToSelectTeacher = (attemptNumber = 0) => {
                const currentTeachers = teachersRef.current;
                console.log(`[TeacherSelector] Attempt ${attemptNumber + 1} to select teacher. Current teachers count:`, currentTeachers.length);

                let teacherToAdd: Teacher | undefined;

                if (newTeacher && newTeacher.id) {
                    // Teacher was returned from the API
                    console.log('[TeacherSelector] Using returned teacher:', newTeacher);
                    teacherToAdd = {
                        id: newTeacher.id,
                        name: newTeacher.name || teacherName,
                        email: newTeacher.email || teacherEmail
                    };
                } else {
                    // Teacher wasn't returned, try to find it in the teachers list
                    console.log('[TeacherSelector] Searching for teacher in list by name or email:', { teacherName, teacherEmail });
                    teacherToAdd = currentTeachers.find(t =>
                        t.name === teacherName || t.email === teacherEmail
                    );
                    console.log('[TeacherSelector] Found teacher in list:', teacherToAdd);
                }

                if (teacherToAdd) {
                    console.log('[TeacherSelector] Selecting teacher:', teacherToAdd);
                    onChange([...selectedTeachersRef.current, teacherToAdd]);
                } else if (attemptNumber < 5) {
                    // Try again after a delay (max 5 attempts)
                    console.log('[TeacherSelector] Teacher not found yet, retrying in 100ms...');
                    setTimeout(() => attemptToSelectTeacher(attemptNumber + 1), 100);
                } else {
                    console.warn('[TeacherSelector] Could not find newly added teacher after multiple attempts');
                }
            };

            setTimeout(() => attemptToSelectTeacher(), 100);
        } catch (error) {
            console.error('Error adding teacher:', error);
            showError(`Failed to add teacher: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsAddingTeacher(false);
        }
    };

    const getButtonText = () => {
        if (selectedTeachers.length === 0) return placeholder;
        if (selectedTeachers.length === 1) return selectedTeachers[0]?.name || placeholder;
        return `${selectedTeachers.length} selected`;
    };

    const filteredTeachers = useMemo(() => {
        const baseList = teachers.filter(teacher => teacher.name && teacher.name.trim() !== '');
        if (!searchTerm.trim()) {
            return baseList;
        }
        const normalized = searchTerm.trim().toLowerCase();
        return baseList.filter(teacher => {
            const nameMatch = teacher.name?.toLowerCase().includes(normalized);
            const emailMatch = teacher.email?.toLowerCase().includes(normalized);
            return nameMatch || emailMatch;
        });
    }, [teachers, searchTerm]);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    }, []);

    const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && filteredTeachers.length > 0) {
            e.preventDefault();
            const firstTeacher = filteredTeachers[0];
            if (!firstTeacher) return;
            handleOptionToggle(firstTeacher);
        }
    }, [filteredTeachers, handleOptionToggle]);

    // Panel JSX to be portaled
    const DropdownPanel = isOpen ? (
        <div
            id="teacher-selector-panel"
            style={panelStyle}
            className="bg-white shadow-lg rounded-md border border-gray-200 flex flex-col overflow-hidden"
        >
            <div className="p-2 border-b border-gray-200 bg-gray-50">
                <Input
                    id="teacher-selector-search"
                    name="teacher-selector-search"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Filter teachers..."
                    autoFocus
                    required={false}
                    minLength={0}
                    label=""
                />
            </div>
            {/* Scrollable teachers list */}
            <div className="overflow-y-auto max-h-60">
                <ul className="py-1">
                    {filteredTeachers.length > 0 ? (
                        filteredTeachers.map(teacher => (
                            <li
                                key={teacher.id}
                                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer flex items-center"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleOptionToggle(teacher);
                                }}
                                role="option"
                                aria-selected={selectedTeachers.some(t => t.id === teacher.id)}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedTeachers.some(t => t.id === teacher.id)}
                                    readOnly
                                    className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer pointer-events-none"
                                    tabIndex={-1}
                                />
                                <span>{teacher.name}</span>
                            </li>
                        ))
                    ) : (
                        <li className="px-3 py-2 text-sm text-gray-500 italic">No teachers available</li>
                    )}
                </ul>
            </div>

            {/* Fixed Add Teacher Option at the bottom */}
            {onAddTeacher && (
                <div className="border-t border-gray-200 flex-shrink-0 bg-white">
                    <div
                        className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 cursor-pointer flex items-center"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            handleAddTeacherClick(e);
                        }}
                    >
                        <PlusIcon />
                        <span className="ml-2 font-medium">Add New Teacher</span>
                    </div>
                </div>
            )}
        </div>
    ) : null;

    return (
        <div className="inline-block w-full flex items-center gap-1" ref={dropdownRef}>
            <div className="flex-grow relative" ref={buttonRef}>
                <Button
                    type="button"
                    variant="secondary"
                    onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        if (!disabled) {
                            if (!isOpen) {
                                updatePanelPosition();
                                setIsOpen(true);
                            } else {
                                setIsOpen(false);
                            }
                        }
                    }}
                    disabled={disabled}
                    title="Select teachers"
                    aria-label="Select teachers"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    className="w-full flex items-center text-left px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <span className="flex-grow truncate">{getButtonText()}</span>
                    <ChevronDownIcon />
                </Button>
            </div>

            {/* Clear Button */}
            {selectedTeachers.length > 0 && !disabled && (
                <button
                    type="button"
                    onClick={handleClearSelection}
                    className="p-2 text-gray-500 hover:text-red-600 focus:outline-none focus:ring-1 focus:ring-red-500 rounded-md flex-shrink-0"
                    title="Clear selection"
                    aria-label="Clear selection"
                >
                    <CrossIcon />
                </button>
            )}

            {/* Render the panel using a Portal */}
            {DropdownPanel && createPortal(DropdownPanel, document.body)}

            {/* Add Teacher Modal */}
            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add New Teacher">
                <div className="space-y-4">
                    <div>
                        <label htmlFor="add-teacher-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <Input
                            id="add-teacher-name"
                            name="name"
                            value={newTeacherData.name}
                            onChange={handleAddTeacherInputChange}
                            placeholder="Teacher's full name"
                            required
                            autoFocus
                            disabled={isAddingTeacher}
                            label=""
                            minLength={0}
                        />
                    </div>
                    <div>
                        <label htmlFor="add-teacher-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <Input
                            id="add-teacher-email"
                            name="email"
                            type="email"
                            value={newTeacherData.email}
                            onChange={handleAddTeacherInputChange}
                            placeholder="teacher@example.com"
                            required
                            disabled={isAddingTeacher}
                            label=""
                            autoFocus={false}
                            minLength={0}
                        />
                    </div>
                    <div className="mt-6 flex justify-end space-x-3">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setIsAddModalOpen(false)}
                            disabled={isAddingTeacher}
                            title="Cancel adding teacher"
                            aria-label="Cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            disabled={isAddingTeacher || !newTeacherData.name.trim() || !newTeacherData.email.trim()}
                            onClick={handleSaveNewTeacher}
                            title={
                                !newTeacherData.name.trim() || !newTeacherData.email.trim()
                                    ? "Please enter both name and email"
                                    : "Add new teacher"
                            }
                            aria-label="Add teacher"
                        >
                            {isAddingTeacher ? 'Adding...' : 'Add Teacher'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default TeacherSelector;
